/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge } from "rizzui";
import { PiEyeDuotone as EyeIcon, PiShoppingCartDuotone as OrdersIcon } from "react-icons/pi";
import { AvatarCard, BucketBar } from "@steelyard/theme";
import { RowActions } from "../../components/table/row-actions";
import { displayAmount, moneyOf, toBuckets, type AgeingRow } from "../../components/customers/customer-ageing-table";
import { customerPath } from "../../lib/paths";

/**
 * One row per customer. The ageing profile is `BucketBar` over whatever buckets the API returned —
 * no column per bucket, because the bucket set is configuration. Outstanding sorts on the API's own
 * figure; the badge is the API's own `overLimit`, never a client comparison.
 */
export const ageingColumns: ColumnDef<AgeingRow>[] = [
  {
    id: "customer",
    accessorKey: "displayName",
    header: "Customer",
    size: 280,
    cell: ({ row }) => (
      <Link to={customerPath(row.original.customerNumber)} className="hover:underline">
        <AvatarCard
          src=""
          name={row.original.displayName}
          description={
            row.original.overLimit ? (
              <Badge size="sm" variant="flat" color="danger">
                Over credit limit
              </Badge>
            ) : undefined
          }
        />
      </Link>
    ),
  },
  {
    id: "ageing",
    header: "Ageing profile",
    size: 460,
    enableSorting: false,
    cell: ({ row }) => <BucketBar buckets={toBuckets(row.original.buckets)} label={`Ageing of ${row.original.displayName}'s outstanding balance`} />,
  },
  {
    id: "outstanding",
    // The API's decimal parsed so the sort compares amounts; nothing computed.
    accessorFn: (row) => moneyOf(row.outstanding) ?? undefined,
    sortUndefined: "last",
    header: "Outstanding",
    size: 170,
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-lexend text-base font-semibold text-gray-900">{displayAmount(row.original.outstanding)}</span>,
  },
  {
    id: "actions",
    header: "",
    size: 100,
    cell: ({ row }) => (
      <RowActions
        name={row.original.displayName}
        primary={{ label: "View customer", icon: EyeIcon, to: customerPath(row.original.customerNumber) }}
        more={[{ label: "Orders from this customer", icon: OrdersIcon, to: `/orders?customerId=${row.original.customerId}` }]}
      />
    ),
  },
];
